// Validation utilities for duplicates merge form

/**
 * Validates that required fields are selected for merge
 * @param {Object} formData - The form data from react-hook-form
 * @param {string} formKey - The unique form key for this duplicate
 * @param {Object} primaryProfile - Primary profile data
 * @param {Object} duplicateProfile - Duplicate profile data
 * @param {string|null} selectedProfileId - The ID of the selected profile to keep
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 */
export const validateMergeForm = (
  formData,
  formKey,
  primaryProfile,
  duplicateProfile,
  selectedProfileId,
) => {
  const errors = [];

  // Validate Profile Selection (required)
  if (!selectedProfileId) {
    errors.push('Please select which profile you wish to keep before merging.');
  }

  // Validate Name selection (required)
  const primaryNameSelected = formData[`${formKey}_primaryName`];
  const duplicateNameSelected = formData[`${formKey}_duplicateName`];

  // Check for shared name field (when names are identical)
  const sharedNameSelected =
    primaryProfile.name === duplicateProfile.name &&
    formData[
      `${formKey}_shared_name_${primaryProfile.name.replace(
        /[^a-zA-Z0-9]/g,
        '_',
      )}`
    ];

  if (!primaryNameSelected && !duplicateNameSelected && !sharedNameSelected) {
    errors.push('Please select a name to use for the merged contact.');
  }

  // Validate Preferred Name selection (only if both profiles have preferred names)
  const hasPrimaryPreferredName = !!primaryProfile.preferredName;
  const hasDuplicatePreferredName = !!duplicateProfile.preferredName;

  // Only validate preferred name if either profile has one
  if (hasPrimaryPreferredName || hasDuplicatePreferredName) {
    const primaryPreferredNameSelected =
      formData[`${formKey}_primaryPreferredName`];
    const duplicatePreferredNameSelected =
      formData[`${formKey}_duplicatePreferredName`];

    // Check for shared preferred name field (when preferred names are identical)
    const sharedPreferredNameSelected =
      primaryProfile.preferredName &&
      duplicateProfile.preferredName &&
      primaryProfile.preferredName === duplicateProfile.preferredName &&
      formData[
        `${formKey}_shared_name_${primaryProfile.preferredName.replace(
          /[^a-zA-Z0-9]/g,
          '_',
        )}`
      ];

    if (
      !primaryPreferredNameSelected &&
      !duplicatePreferredNameSelected &&
      !sharedPreferredNameSelected
    ) {
      errors.push(
        'Please select a preferred name to use for the merged contact.',
      );
    }
  }

  // Validate Email selection (required if emails are available and not auto-selected)
  const hasEmails =
    primaryProfile.emails?.length > 0 || duplicateProfile.emails?.length > 0;
  if (hasEmails) {
    const allEmails = [
      ...primaryProfile.emails.map((e) => e.emailAddress),
      ...duplicateProfile.emails.map((e) => e.emailAddress),
    ];
    const uniqueEmailsCount = [...new Set(allEmails)].length;

    // Only validate if emails are not auto-selected (>3 unique emails)
    if (uniqueEmailsCount > 3) {
      let hasEmailSelected = false;

      // Check primary emails
      primaryProfile.emails?.forEach((_, index) => {
        if (formData[`${formKey}_primaryEmail${index}`]) {
          hasEmailSelected = true;
        }
      });

      // Check duplicate emails
      duplicateProfile.emails?.forEach((_, index) => {
        if (formData[`${formKey}_duplicateEmail${index}`]) {
          hasEmailSelected = true;
        }
      });

      // Check shared emails (when emails are identical)
      primaryProfile.emails?.forEach((email) => {
        const sanitizedEmail = email.emailAddress
          .toLowerCase()
          .replace(/[^a-zA-Z0-9]/g, '_');
        if (formData[`${formKey}_shared_email_${sanitizedEmail}`]) {
          hasEmailSelected = true;
        }
      });

      if (!hasEmailSelected) {
        errors.push('Please select at least one email address.');
      }
    }

    // Validate maximum 3 unique emails selected
    const selectedEmails = new Set();
    primaryProfile.emails?.forEach((email, index) => {
      if (formData[`${formKey}_primaryEmail${index}`]) {
        selectedEmails.add(email.emailAddress.toLowerCase());
      }
    });
    duplicateProfile.emails?.forEach((email, index) => {
      if (formData[`${formKey}_duplicateEmail${index}`]) {
        selectedEmails.add(email.emailAddress.toLowerCase());
      }
    });
    // Check shared emails
    primaryProfile.emails?.forEach((email) => {
      const sanitizedEmail = email.emailAddress
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, '_');
      if (formData[`${formKey}_shared_email_${sanitizedEmail}`]) {
        selectedEmails.add(email.emailAddress.toLowerCase());
      }
    });

    if (selectedEmails.size > 3) {
      errors.push('Please select a maximum of 3 unique email addresses.');
    }
  }

  // Validate Phone selection (required if phones are available and not auto-selected)
  const hasPhones =
    primaryProfile.phones?.length > 0 || duplicateProfile.phones?.length > 0;
  if (hasPhones) {
    const allPhones = [
      ...primaryProfile.phones.map((p) => p.phoneNumber),
      ...duplicateProfile.phones.map((p) => p.phoneNumber),
    ];
    const uniquePhonesCount = [...new Set(allPhones)].length;

    // Only validate if phones are not auto-selected (>3 unique phones)
    if (uniquePhonesCount > 3) {
      let hasPhoneSelected = false;

      // Check primary phones
      primaryProfile.phones?.forEach((_, index) => {
        if (formData[`${formKey}_primaryPhone${index}`]) {
          hasPhoneSelected = true;
        }
      });

      // Check duplicate phones
      duplicateProfile.phones?.forEach((_, index) => {
        if (formData[`${formKey}_duplicatePhone${index}`]) {
          hasPhoneSelected = true;
        }
      });

      // Check shared phones (when phones are identical)
      primaryProfile.phones?.forEach((phone) => {
        const sanitizedPhone = phone.phoneNumber.replace(/[^a-zA-Z0-9]/g, '_');
        if (formData[`${formKey}_shared_phone_${sanitizedPhone}`]) {
          hasPhoneSelected = true;
        }
      });

      if (!hasPhoneSelected) {
        errors.push('Please select at least one phone number.');
      }
    }

    // Validate maximum 3 unique phones selected
    const selectedPhones = new Set();
    primaryProfile.phones?.forEach((phone, index) => {
      if (formData[`${formKey}_primaryPhone${index}`]) {
        selectedPhones.add(phone.phoneNumber);
      }
    });
    duplicateProfile.phones?.forEach((phone, index) => {
      if (formData[`${formKey}_duplicatePhone${index}`]) {
        selectedPhones.add(phone.phoneNumber);
      }
    });
    // Check shared phones
    primaryProfile.phones?.forEach((phone) => {
      const sanitizedPhone = phone.phoneNumber.replace(/[^a-zA-Z0-9]/g, '_');
      if (formData[`${formKey}_shared_phone_${sanitizedPhone}`]) {
        selectedPhones.add(phone.phoneNumber);
      }
    });

    if (selectedPhones.size > 3) {
      errors.push('Please select a maximum of 3 unique phone numbers.');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Gets validation error messages for display
 * @param {Array<string>} errors - Array of error messages
 * @returns {string} - Formatted error message for display
 */
export const formatValidationErrors = (errors) => {
  if (errors.length === 0) return '';

  if (errors.length === 1) {
    return errors[0];
  }

  return `Please address the following issues:\n• ${errors.join('\n• ')}`;
};
